import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Page,
  Layout,
  Filters,
  Select,
  EmptyState,
  Button,
  Spinner,
} from "@shopify/polaris";
import useIsPhone from "features/files/use-is-phone";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Outlet } from "react-router-dom";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchAssets,
  fetchMoreAssets,
  setFilterValue,
} from "containers/assets/assets.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import { EditIcon, PersonAddIcon, ViewIcon } from "@shopify/polaris-icons";
import AssetsTypesSelect from "components/assets-types-select/assets-types-select";
import UserSelect from "features/user-select/user-select";
import { fetch as fetchUsers } from "services/user-select.service";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import AssetsTabs from "features/asset-requests/AssetsTabs";

const END_REACHED_THRESHOLD = 200;

function AssetsContainer() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assetId, companyId } = useParams();
  const isPhone = useIsPhone();
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});

  const {
    collection,
    filtersValue,
    assetTypes,
    isLoading,
    isLoadingMore,
    isError,
    pagination,
  } = useSelector((state: RootState) => state.assetsReducer);

  const headerRef = useRef<HTMLDivElement>(null); // filters region (ResizeObserver)
  const listWrapRef = useRef<HTMLDivElement>(null); // wraps the table (measures top + hosts scroll listener)
  const sentinelRef = useRef<HTMLDivElement>(null); // infinite-scroll sentinel (phone)
  const [listMaxHeight, setListMaxHeight] = useState("calc(100vh - 280px)");

  const hasNextPage = Boolean(pagination?.hasNextPage);
  const nextPage = (pagination?.page || 1) + 1;

  const assetCollection =
    collection?.map((item: any) => ({
      id: item.id,
      title: item?.latestFileEntity?.title, // latest file title
      latestFileEntity: item?.latestFileEntity,
      name: item.name,
      type: item.type,
      brand: item.brand,
      model: item.model,
      serial: item.serial,
      status: item.status,
      assigned_to: item.assigned_to,
      purchase_date: item.purchase_date,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    })) || [];

  const onRowClick = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("AssetsForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handlePageRedirect = ({ id }: { id: string }) => {
    if (id) {
      navigate(`/app/${companyId}/assets/${id}`);
    }
  };

  const handlePdfView = async (id: string, companyId: string | any) => {
    if (id && companyId) {
      dispatch(setModalxComponent("PdfView"));
      dispatch(setModalxStyle("large"));
      dispatch(
        setModalxComponentProps({
          id,
          companyId,
        })
      );
    }
  };

  const handleCreateAsset = () => {
    dispatch(setModalxComponent("AssetsForm"));
  };

  const handleAssignUser = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("UserAssignToAssetsForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const disambiguateLabel = (key: string, value: any) => {
    switch (key) {
      case "textSearch":
        return `${t("search")}: ${t(value)}`;
      case "assetTypes":
        return `${t("assetTypes")} ${assetTypes?.[value] || value}`;
      case "user": {
        const values = Array.isArray(value) ? value : [value];
        const labels = values
          .filter(Boolean)
          .map((userId: string) => usersMap[userId] || userId)
          .join(", ");
        return `${t("assigned_user")} ${labels}`;
      }
      case "stock":
        return `${t("stock")} ${value}`;
      default:
        return value;
    }
  };

  // Initial load + reload page 1 whenever company or filters change.
  useEffect(() => {
    if (companyId) dispatch(fetchAssets(companyId, filtersValue));
  }, [dispatch, filtersValue, companyId]);

  // Cap the table body height so only the list scrolls and the column headers
  // stick (uses DataTable's existing maxBodyHeight, no component change).
  useLayoutEffect(() => {
    const compute = () => {
      const el = listWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = window.innerHeight - top - 24;
      setListMaxHeight(`${Math.max(Math.round(available), 240)}px`);
    };

    compute();
    window.addEventListener("resize", compute);

    let resizeObserver: ResizeObserver | undefined;
    if (headerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(compute);
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", compute);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const loadMore = () => {
    if (companyId && hasNextPage && !isLoading && !isLoadingMore) {
      dispatch(fetchMoreAssets(companyId, filtersValue, nextPage));
    }
  };

  // Desktop infinite scroll: listen to the DataTable's internal scroll area
  // (the element that also makes the header sticky).
  useEffect(() => {
    if (isPhone) return undefined;
    const wrap = listWrapRef.current;
    if (!wrap) return undefined;
    const scrollEl = wrap.querySelector<HTMLElement>(".overflow-y-auto");
    if (!scrollEl) return undefined;

    const onScroll = () => {
      if (isLoading || isLoadingMore || !hasNextPage) return;
      if (
        scrollEl.scrollTop + scrollEl.clientHeight >=
        scrollEl.scrollHeight - END_REACHED_THRESHOLD
      ) {
        loadMore();
      }
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // auto-fill if the first page doesn't fill the viewport
    return () => scrollEl.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    filtersValue,
    hasNextPage,
    isLoading,
    isLoadingMore,
    collection.length,
    isPhone,
  ]);

  // Phone infinite scroll: cards flow in the page, so observe a sentinel
  // against the viewport.
  useEffect(() => {
    if (!isPhone) return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filtersValue, hasNextPage, isLoading, isLoadingMore, isPhone]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!companyId) {
        setUsersMap({});
        return;
      }
      try {
        const data = await fetchUsers(companyId, {}, false);
        const mapped = (data?.usersCollection || []).reduce(
          (
            acc: Record<string, string>,
            user: { id: string; name?: string; email?: string }
          ) => {
            if (user?.id) {
              acc[user.id] = user?.name || user?.email || user.id;
            }
            return acc;
          },
          {}
        );
        setUsersMap(mapped);
      } catch (error) {
        setUsersMap({});
      }
    };

    loadUsers();
  }, [companyId]);

  const handleFilterChange = (value: any, key: string) => {
    dispatch(setFilterValue({ key, value }));
  };
  const handleQueryChange = (value: string) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };
  const handleQueryValueRemove = () => {
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };
  const handleClearAll = () => {};
  const appliedFilters: any = [];
  Object.keys(filtersValue).forEach((key) => {
    if (filtersValue[key] && filtersValue[key].length > 0) {
      appliedFilters.push({
        key,
        label: disambiguateLabel(key, filtersValue[key]),
        onRemove: () => handleFilterChange(null, key),
      });
    }
  });

  const filters = [
    {
      key: "assetTypes",
      label: t("assets_types"),
      filter: (
        <AssetsTypesSelect
          value={filtersValue.assetTypes}
          onChange={(val: any) => handleFilterChange(val, "assetTypes")}
          isRequired={false}
        />
      ),
    },
    {
      key: "user",
      label: t("assigned_user"),
      filter: (
        <UserSelect
          value={filtersValue.user || ""}
          name="user"
          onChange={handleFilterChange}
          isRequired={false}
          allowMultiple={true}
          label={"assigned_user"}
        />
      ),
    },
    {
      key: "stock",
      label: t("select_stock"),
      filter: (
        <Select
          label={t("stock_select")}
          name="stock"
          options={[
            {
              label: t("asset_in_stock"),
              value: "in stock",
            },
            {
              label: t("asset_in_use"),
              value: "in use",
            },
          ]}
          onChange={(val: any) => handleFilterChange(val, "stock")}
          value={filtersValue.stock || ""}
        />
      ),
    },
  ];

  // On a phone the whole card opens the asset (rowUrl below); on wider screens
  // the cell texts do.
  const openAsset = isPhone ? undefined : handlePageRedirect;

  const columns: Column[] = [
    { key: "#" },
    {
      key: "name",
      name: t("asset_name"),
      onClick: openAsset,
      mobileView: "topLeft",
    },
    {
      key: "type.title",
      name: t("asset_type"),
      onClick: openAsset,
      translationRequired: true,
    },
    {
      key: "brand",
      name: t("asset_brand"),
      onClick: openAsset,
      translationRequired: true,
    },
    {
      key: "model",
      name: t("asset_model"),
      onClick: openAsset,
      translationRequired: true,
    },
    {
      key: "serial",
      name: t("asset_serial"),
      onClick: openAsset,
      translationRequired: true,
    },
    {
      key: "status",
      name: t("status"),
      onClick: openAsset,
      translationRequired: true,
      mobileView: "topRight",
    },
    {
      key: "assigned_to.name",
      name: t("asset_assigned_to"),
      onClick: openAsset,
      translationRequired: true,
      mobileView: "bottomLeft",
    },
    {
      key: "purchase_date",
      name: t("asset_purchase_date"),
      type: "date",
      onClick: openAsset,
    },
    {
      key: "view",
      name: t("view"),
      onClick: (item: any) => handlePdfView(item?.latestFileEntity?.id, companyId),
      type: "specificAction",
      Icon: ViewIcon,
    },
    {
      key: "Edit",
      name: t("edit"),
      onClick: (item: any) => onRowClick({ id: item.id }),
      type: "actionBtn",
      Icon: EditIcon,
    },
    {
      key: "assign_user",
      name: t("asset_assign_user"),
      onClick: (item: any) => handleAssignUser({ id: item.id }),
      type: "actionBtn",
      Icon: PersonAddIcon,
    },
  ];

  // A phone card has four slots, so type, brand and model share the last one.
  if (isPhone) {
    columns.push({
      key: "summary",
      type: "custom",
      mobileView: "bottomRight",
      render: (item: any) =>
        [item.type?.title && t(item.type.title), item.brand, item.model]
          .filter(Boolean)
          .join(" · "),
    });
  }

  const filtersNode = (
    <Filters
      queryValue={filtersValue.textSearch}
      filters={filters}
      appliedFilters={appliedFilters}
      onQueryChange={handleQueryChange}
      onQueryClear={handleQueryValueRemove}
      onClearAll={handleClearAll}
    />
  );

  const showSkeleton = isLoading && assetCollection.length === 0;
  const showEmpty = !isLoading && !isError && assetCollection.length === 0;

  let content = <Outlet />;

  if (!assetId) {
    content = (
      <Page
        fullWidth
        primaryAction={{
          content: t("asset_create"),
          onAction: handleCreateAsset,
        }}
        title={t("assets")}
        titleMetadata={
          <>
            <AuditLogPopover subjectType="Asset" />
            {isLoadingMore && (
              <Spinner
                accessibilityLabel={t("loading", { defaultValue: "Loading" })}
                size="small"
              />
            )}
          </>
        }
      >
        <div ref={headerRef} style={{ padding: "16px", display: "flex" }}>
          <div style={{ flex: 1 }}>{filtersNode}</div>
        </div>

        <div ref={listWrapRef}>
          {showSkeleton ? (
            <DataTableSkeleton
              rows={10}
              columns={columns.length}
              iconColumnIndexes={[9, 10, 11]}
            />
          ) : isError ? (
            <EmptyState
              heading={t("not_fetch", {
                defaultValue: "Could not load assets",
              })}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <Button
                onClick={() =>
                  companyId && dispatch(fetchAssets(companyId, filtersValue))
                }
              >
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </EmptyState>
          ) : showEmpty ? (
            <EmptyState
              heading={t("no_assets", { defaultValue: "No assets found" })}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            />
          ) : (
            <>
              <DataTable
                collection={assetCollection}
                columns={columns}
                rowUrl={
                  isPhone
                    ? (item) => `/app/${companyId}/assets/${item.id}`
                    : undefined
                }
                maxBodyHeight={isPhone ? undefined : listMaxHeight}
              />
              {/* Sentinel for phone infinite scroll (page-level scroll) */}
              <div ref={sentinelRef} style={{ height: 1 }} />
            </>
          )}
        </div>
      </Page>
    );
  }

  return (
    <Layout>
      <Layout.Section>
        {!assetId && companyId && (
          <div className="px-4 pt-4 sm:px-8">
            <AssetsTabs companyId={companyId} />
          </div>
        )}
        {content}
      </Layout.Section>
    </Layout>
  );
}

export default AssetsContainer;
